import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

export interface DetailPageHeaderProps {
  /** Page title (translated). */
  title: string;
  /** Back-navigation handler. */
  onBack: () => void;
  /** When provided, renders a standard Edit button. */
  onEdit?: () => void;
  /** When provided, renders a standard (error-colored) Delete button. */
  onDelete?: () => void;
  /** Extra right-side actions rendered before the edit/delete buttons. */
  actions?: ReactNode;
  /** aria-label for the back button. Defaults to the common `back` string. */
  backAriaLabel?: string;
}

/**
 * Shared detail-page header: a back button, the title and right-aligned
 * edit/delete actions. Unifies the header used by PersonDetailPage and
 * CompanyDetailPage (issue #881).
 */
export function DetailPageHeader({
  title,
  onBack,
  onEdit,
  onDelete,
  actions,
  backAriaLabel,
}: DetailPageHeaderProps) {
  const { t: tc } = useTranslation('common');

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
      <Tooltip title={backAriaLabel ?? tc('back')}>
        <IconButton onClick={onBack} aria-label={backAriaLabel ?? tc('back')}>
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
      <Typography variant="h4" fontWeight={600} sx={{ flexGrow: 1, minWidth: 0 }}>
        {title}
      </Typography>
      {actions}
      {onEdit && (
        <Button startIcon={<EditIcon />} onClick={onEdit}>
          {tc('edit')}
        </Button>
      )}
      {onDelete && (
        <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
          {tc('delete')}
        </Button>
      )}
    </Stack>
  );
}
